import { t } from '@lingui/macro'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSWRConfig } from 'swr'

import { isBetween } from '@/@bothrs/utils/number'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { mixpanel } from '@/mixpanel'
import { ErrorLayout } from '@/modules/shared/components/error-layout/ErrorLayout'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { intramurosBase, isIntramuros } from '@/utils/intramuros'
import { CloseQuestionnaireModal } from '../../components/close-questionnaire-modal/CloseQuestionnaireModal'
import { Questionnaire } from '../../components/questionnaire/Questionnaire'
import { QuestionnaireCompleted } from '../../components/questionnaire-completed/QuestionnaireCompleted'
import { QuestionnaireEntry } from '../../components/questionnaire-entry/QuestionnaireEntry'
import { clearQuestionnaireActivityCache } from '../../helpers/questionnaire-cache.helpers'
import { linearStrategy } from '../../hooks/questionnaire-flow-strategy'
import { useAnimationDirection } from '../../hooks/useAnimationDirection'
import { useQuestionnaireFlow } from '../../hooks/useQuestionnaireFlow'
import {
  type QuestionnaireBackInterceptor,
  QuestionnaireBackInterceptorProvider,
} from '../../providers/QuestionnaireBackInterceptor'

import type { QuestionAnswerInput } from '@healthblocks-io/core'
import type { SwipeEventData } from 'react-swipeable'

/**
 * QuestionnaireFlowPage
 *
 * High-level orchestration component that renders and manages the user-facing
 * questionnaire flow. This is the "screen" component consumers interact with.
 *
 * Architecture:
 *  - **Event-Driven**: Receives submission events from the <Questionnaire> component.
 *  - **Persistence Strategy**:
 *    - `handleSubmitAnswer`: Calls `setAnswer` (Engine) to update local state, validations, and skip logic on every change.
 *    - `handleGoNext`: Explicitly calls `saveProgress` (API) before navigating to the next question.
 *    - `submitQuestionnaire`: Called on the final step to mark the questionnaire as completed.
 *
 * Responsibilities:
 *  - Displays header, progress bar, back/close buttons
 *  - Handles questionnaire initialization and navigation transitions
 *  - Supports swipe gestures for navigation (e.g. swipe to go back)
 *  - Renders intro/outro "Display" screens when applicable
 *  - Passes navigation callbacks (`onNext`, `onPrevious`) to <Questionnaire>
 *  - Orchestrates the "Saving" state during transitions
 *  - Opens/closes the modal when the user attempts to exit the questionnaire
 *
 * Must be rendered inside <QuestionnaireEngineProvider>.
 *
 * Example:
 * <QuestionnaireFlowPage />
 */
export function QuestionnaireFlowPage() {
  // Refs
  // ------------------------------------------------------------------------- /
  // Used to scroll to the top of the page
  const pageLayoutMainRef = useRef<HTMLDivElement>(null)

  // State
  // ------------------------------------------------------------------------- /
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState(false)

  // Hooks
  // ------------------------------------------------------------------------- /
  const {
    state,
    currentIndex,
    totalQuestions,
    maxPossibleQuestions,
    globalIndex,
    currentQuestion,
    goBack,
    goNext,
    setAnswer,
  } = useQuestionnaireFlow(linearStrategy)

  const { showSuccessToast, showErrorToast } = useToast()
  const isLoggedInForMandatee = useLoggedInForMandatee()

  const navigate = useNavigate()
  const { activityId } = useParams()
  const { mutate } = useSWRConfig()

  const animationDirection = useAnimationDirection(currentIndex)

  // Template variables
  // ------------------------------------------------------------------------- /
  const calculatedProgress: { min: number; max: number; value: number } = useMemo(() => {
    return {
      min: 0,
      max: maxPossibleQuestions,
      value: globalIndex + 1,
    }
  }, [maxPossibleQuestions, globalIndex])

  // Check if this is the entry (first Display question)
  const shouldRenderEntryPage = useMemo(() => {
    return (
      currentIndex === 0 && currentQuestion?.type === 'Display' && state.answerableQuestions.at(0)?.type === 'Display'
    )
  }, [currentIndex, currentQuestion?.type, state.answerableQuestions])

  // Check if this is the completed (last Display question)
  const shouldRenderCompletedPage = useMemo(() => {
    const hasIntro = state.answerableQuestions.at(0)?.type === 'Display'
    const hasOutro = state.answerableQuestions.at(-1)?.type === 'Display'
    const max = totalQuestions - (hasIntro ? 1 : 0) - (hasOutro ? 1 : 0)
    const value = currentIndex + (hasIntro ? 0 : 1)

    return value > max && currentQuestion?.type === 'Display' && state.answerableQuestions.at(-1)?.type === 'Display'
  }, [currentIndex, totalQuestions, currentQuestion?.type, state.answerableQuestions])

  // Interaction methods
  // ------------------------------------------------------------------------- /

  const scrollToTop = () => {
    pageLayoutMainRef?.current?.scrollTo({ top: 0 })
  }

  // Lets the active question consume the back action (e.g. the medication /
  // allergy questions returning from their add-flow to the initial choice).
  // Only when no question handles it do we navigate to the previous question.
  // The boolean mirror is reactive so the back control appears even when there
  // is no previous question to navigate to (the question handles back itself).
  const backInterceptorRef = useRef<QuestionnaireBackInterceptor | null>(null)
  const [hasBackInterceptor, setHasBackInterceptor] = useState(false)
  const setBackInterceptor = useCallback((interceptor: QuestionnaireBackInterceptor | null) => {
    backInterceptorRef.current = interceptor
    setHasBackInterceptor(interceptor !== null)
  }, [])
  const backInterceptorValue = useMemo(() => ({ setBackInterceptor }), [setBackInterceptor])

  // Back is available when a question can handle it internally, or when there
  // is a previous question to navigate to.
  const canGoBack = hasBackInterceptor || currentIndex > 0

  const handleGoBack = () => {
    // A question may consume the back action (e.g. form → choice); only navigate
    // to the previous question when it doesn't. Either way scroll to the top so
    // the new view starts from the top.
    const handled = backInterceptorRef.current?.() ?? false
    if (!handled) goBack()
    scrollToTop()
  }

  const navigateAfterCompletion = () => {
    if (isIntramuros) {
      navigate(intramurosBase, {
        replace: true,
        state: { tab: 'resolved' },
      })
    } else {
      navigate(-1)
    }
  }

  const handleGoNext = async () => {
    mixpanel.track('questionnaire_question_completed', {
      questionnaire_slug: state.questionnaire.name,
      question_linkId: currentQuestion?.linkId,
      question_type: currentQuestion?.type,
      question_index: globalIndex,
      total_questions: maxPossibleQuestions,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
    })

    setIsSaving(true)

    try {
      const goNextResult = await goNext()

      switch (goNextResult.type) {
        case 'COMPLETED': {
          showSuccessToast({
            message: t({
              id: 'questionnaire.success',
              comment: 'Questionnaire success message',
              message: 'Vragenlijst verstuurd',
            }),
          })
          navigateAfterCompletion()
          break
        }
        case 'ERROR': {
          showErrorToast({
            message: t({
              id: 'questionnaire.error',
              comment: 'Questionnaire error message',
              message: 'Er ging iets mis bij het versturen van de vragenlijst',
            }),
          })
          break
        }
        default: {
          break
        }
      }
    } catch {
      showErrorToast({
        message: t({
          id: 'questionnaire.save-progress.error',
          comment: 'Error saving questionnaire progress',
          message: 'Er ging iets mis bij het opslaan. Probeer het opnieuw.',
        }),
      })
    } finally {
      setIsSaving(false)
      scrollToTop()
    }
  }

  const handleSubmitAnswer = async (answer: QuestionAnswerInput, autoAdvance?: boolean) => {
    if (autoAdvance) {
      mixpanel.track('questionnaire_question_completed', {
        questionnaire_slug: state.questionnaire.name,
        question_linkId: currentQuestion?.linkId,
        question_type: currentQuestion?.type,
        question_index: globalIndex,
        total_questions: maxPossibleQuestions,
        is_mandate: isLoggedInForMandatee,
        is_intramuros: isIntramuros,
      })
    }

    setIsSaving(true)

    try {
      const setAnswerResult = await setAnswer(answer, autoAdvance)

      switch (setAnswerResult.type) {
        case 'COMPLETED': {
          showSuccessToast({
            message: t({
              id: 'questionnaire.success',
              comment: 'Questionnaire success message',
              message: 'Vragenlijst verstuurd',
            }),
          })
          navigateAfterCompletion()
          break
        }
        case 'ERROR': {
          showErrorToast({
            message: t({
              id: 'questionnaire.error',
              comment: 'Questionnaire error message',
              message: 'Er ging iets mis bij het versturen van de vragenlijst',
            }),
          })
          break
        }
        default: {
          break
        }
      }
    } catch {
      showErrorToast({
        message: t({
          id: 'questionnaire.save-progress.error',
          comment: 'Error saving questionnaire progress',
          message: 'Er ging iets mis bij het opslaan. Probeer het opnieuw.',
        }),
      })
    } finally {
      setIsSaving(false)
      if (autoAdvance) {
        scrollToTop()
      }
    }
  }

  const handleSubmitCompletedPage = async (answer: QuestionAnswerInput) => {
    setIsSaving(true)

    try {
      const setAnswerResult = await setAnswer(answer, true)

      switch (setAnswerResult.type) {
        case 'COMPLETED': {
          showSuccessToast({
            message: t({
              id: 'questionnaire.success',
              comment: 'Questionnaire success message',
              message: 'Vragenlijst verstuurd',
            }),
          })
          navigateAfterCompletion()
          break
        }

        case 'ERROR': {
          showErrorToast({
            message: t({
              id: 'questionnaire.error',
              comment: 'Questionnaire error message',
              message: 'Er ging iets mis bij het versturen van de vragenlijst',
            }),
          })
          break
        }
      }
    } catch {
      showErrorToast({
        message: t({
          id: 'questionnaire.save-progress.error',
          comment: 'Error saving questionnaire progress',
          message: 'Er ging iets mis bij het opslaan. Probeer het opnieuw.',
        }),
      })
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Handle swipe gesture
   */
  const handleSwipe = useCallback(
    (event: SwipeEventData) => {
      const initialX = event.initial[0]

      if (
        event.dir === 'Right' &&
        isBetween(initialX, [0, 40]) &&
        canGoBack &&
        !(event.event.target instanceof HTMLInputElement && event.event.target.type === 'range') // Ignore swipes happening on a slider
      ) {
        mixpanel.track('questionnaire_swipe_previous', {
          questionnaire_slug: state.questionnaire.name,
          is_mandate: isLoggedInForMandatee,
        })
        handleGoBack()
      }
    },
    [canGoBack, handleGoBack, isLoggedInForMandatee, state.questionnaire.name],
  )

  const handleCloseQuestionnaire = () => {
    // Track
    mixpanel.track('questionnaire_show_close_warning', {
      questionnaire_slug: state.questionnaire.name,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
    })

    setShowCloseModal(true)
  }

  const handleDismissCloseModal = () => {
    // Track
    mixpanel.track('questionnaire_resume', {
      questionnaire_slug: state.questionnaire.name,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
    })

    setShowCloseModal(false)
  }

  const handleConfirmCloseQuestionnaire = () => {
    // Track
    mixpanel.track('questionnaire_cancel', {
      questionnaire_slug: state.questionnaire.name,
      question_linkId: currentQuestion?.linkId,
      question_index: globalIndex,
      total_questions: maxPossibleQuestions,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
    })

    // Clear the cached activity so the next open always fetches fresh data.
    // This must happen at the navigation exit point (not in the persistence
    // layer) because SWR would notify mounted subscribers with undefined data,
    // causing the parent to render an error screen before navigation completes.
    if (activityId) {
      clearQuestionnaireActivityCache(mutate, activityId)
    }

    navigate(-1)
  }

  // Render
  // ------------------------------------------------------------------------- /

  if (state.status === 'loading') {
    return <AnimatedLoader />
  }

  if (state.status === 'error') {
    return (
      <ErrorLayout
        title='Er is iets misgegaan bij het laden van de vraaglijst'
        message='Er is iets misgegaan bij het laden van de vraaglijst'
      />
    )
  }

  if (shouldRenderEntryPage) {
    return (
      <QuestionnaireEntry
        question={currentQuestion}
        onGoBack={handleConfirmCloseQuestionnaire}
        onGoNext={handleGoNext}
        isSaving={isSaving}
      />
    )
  }

  if (shouldRenderCompletedPage) {
    return (
      <QuestionnaireCompleted
        question={currentQuestion}
        onSubmitAnswer={handleSubmitCompletedPage}
        onGoBack={handleGoBack}
        isSaving={isSaving}
      />
    )
  }

  return (
    <ContentPageLayout
      showScrollbar={true}
      mainRef={pageLayoutMainRef}
      mainRefId='questionnaire-page'
      showHeaderAlways
      header={
        <AppHeader
          primaryAction={
            canGoBack
              ? {
                  label: t({
                    id: 'globals.previous-question',
                    comment: 'Global for previous question',
                    message: 'Vorige vraag',
                  }),
                  icon: ['far', 'chevron-left'],
                  onClick: handleGoBack,
                }
              : undefined
          }
          secondaryAction={{
            label: t({
              id: 'globals.close',
              comment: 'Global for close',
              message: 'Sluiten',
            }),
            icon: ['far', 'close'],
            onClick: handleCloseQuestionnaire,
          }}
          progressText={state.questionnaire.title}
          progress={calculatedProgress.max <= 0 ? undefined : calculatedProgress}
          progressLayout='inline'
        />
      }
      onSwipe={handleSwipe}
    >
      <QuestionnaireBackInterceptorProvider value={backInterceptorValue}>
        <Questionnaire
          animationDirection={animationDirection}
          currentQuestion={currentQuestion}
          onSubmitAnswer={handleSubmitAnswer}
          onNext={handleGoNext}
          onPrevious={handleGoBack}
          isSaving={isSaving}
        />
      </QuestionnaireBackInterceptorProvider>
      <CloseQuestionnaireModal
        isVisible={showCloseModal}
        onAction={handleConfirmCloseQuestionnaire}
        onClose={handleDismissCloseModal}
      />
    </ContentPageLayout>
  )
}
